import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiTrash2 } from 'react-icons/fi';
import { FaArrowLeft, FaArrowRight, FaExchangeAlt } from 'react-icons/fa';
import { FaPencil } from 'react-icons/fa6';
import { CgSpinner } from 'react-icons/cg';

import Header from '../common/header';
import LoadingComp from '../common/loading';
import { ApiError } from '../common/apiError';
import { userAppData } from '../MainApp';
import BookingstsBookingModal from './bookingstsBookingModal';
import BookingstsCancelModal from './bookingstsCancelModal';
import BookingstsTransferModal from './bookingstsTransferModal';
import BookingstsEditModal from './bookingstsEditModal';

import {
    cancelBookingTs,
    checkBookingTsAvailability,
    createBookingTs,
    editBookingTs,
    getBookingTsDay,
    transferBookingTs,
} from './bookingstsApi';
import {
    buildDisplaySlots,
    formatDateForApi,
    minutesToTime,
    timeToMinutes,
    snapDownToFive,
    snapUpToFive,
} from './bookingstsTimeUtils';

import './bookingstsHome.css';

const toastConfig = { autoClose: 4000, position: 'top-right' };
const DESKTOP_ROOM_PAGE_SIZE = 5;
const MOBILE_ROOM_PAGE_SIZE = 1;

/**
 * Convert a date input string into a local Date object only when the full value is valid.
 * @param {string} dateText - Browser date input value in yyyy-mm-dd format.
 * @returns {Date|null} Parsed local date or null when the input is incomplete/invalid.
 */
function parseDateInputValue(dateText) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateText || ''))) {
        return null;
    }

    const [year, month, day] = dateText.split('-').map(Number);
    const parsedDate = new Date(year, month - 1, day);

    if (
        Number.isNaN(parsedDate.getTime())
        || parsedDate.getFullYear() !== year
        || parsedDate.getMonth() !== (month - 1)
        || parsedDate.getDate() !== day
    ) {
        return null;
    }

    return parsedDate;
}

/**
 * Build room rendering metadata for table rowSpan display from booking ranges.
 * @param {Array<object>} rooms - Permitted room metadata list.
 * @param {object} bookingsByRoom - API bookings grouped by roomGuid.
 * @param {Array<number>} slotStarts - Display slot start minute values.
 * @param {number} slotSize - Display slot size in minutes.
 * @returns {object} Room layout map keyed by roomId.
 */
function buildRoomLayouts(rooms, bookingsByRoom, slotStarts, slotSize) {
    const slotCount = slotStarts.length;
    const dayStartMinutes = slotStarts[0] ?? 0;

    const layouts = {};

    rooms.forEach((room) => {
        const roomBookings = Array.isArray(bookingsByRoom?.[room.roomId]) ? bookingsByRoom[room.roomId] : [];
        const startCells = {};
        const covered = new Set();

        roomBookings
            .slice()
            .sort((first, second) => Number(first.startMins) - Number(second.startMins))
            .forEach((booking) => {
                const rawStartIndex = Math.floor((Number(booking.startMins) - dayStartMinutes) / slotSize);
                const rawEndIndex = Math.ceil((Number(booking.endMins) - dayStartMinutes) / slotSize);

                const startIndex = Math.max(0, rawStartIndex);
                const endIndex = Math.min(slotCount, Math.max(startIndex + 1, rawEndIndex));

                if (startIndex >= slotCount) {
                    return;
                }

                const rowSpan = Math.max(1, endIndex - startIndex);
                startCells[startIndex] = { booking, rowSpan };

                for (let index = startIndex + 1; index < (startIndex + rowSpan); index += 1) {
                    covered.add(index);
                }
            });

        layouts[room.roomId] = { startCells, covered };
    });

    return layouts;
}

/**
 * Check whether the current user can add attendees for the selected room group.
 * Attendee permission is stored per groupAccess entry, not as a top-level flag.
 * @param {object} room - Booking room metadata from the day bookings payload.
 * @returns {boolean} True when the room and user's group permission both allow attendees.
 */
function canAddAttendeesForRoom(room) {
    if (!room?.allowAttendees) {
        return false;
    }

    const roomGroupId = String(room?.groupId || '').trim();
    const groupAccess = Array.isArray(userAppData?.bookingsData?.groupAccess)
        ? userAppData.bookingsData.groupAccess
        : [];

    return groupAccess.some((groupEntry) => (
        String(groupEntry?.id || groupEntry?.groupId || '').trim() === roomGroupId
        && groupEntry?.allowAttendees === true
    ));
}

/**
 * BOOKINGSTS day view page with drag selection and 5-minute lock booking logic.
 * @returns {JSX.Element} BookingTS module UI.
 */
export default function BookingstsHome() {
    const navigate = useNavigate();
    const tableWrapRef = useRef(null);
    const dragCursorRef = useRef({ x: 0, y: 0 });
    const autoScrollFrameRef = useRef(null);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [pendingDateText, setPendingDateText] = useState(() => formatDateForApi(new Date()));
    const [isLoaded, setIsLoaded] = useState(false);
    const [apiError, setApiError] = useState(false);

    const [roomsMetadata, setRoomsMetadata] = useState({ rooms: [], groups: [], subGroups: [] });
    const [bookingsByRoom, setBookingsByRoom] = useState({});
    const [displaySettings, setDisplaySettings] = useState({
        dayStartTime: '08:00',
        dayEndTime: '18:00',
        displaySlotMins: 15,
    });

    const [dragSelection, setDragSelection] = useState(null);
    const [dragCursor, setDragCursor] = useState({ x: 0, y: 0 });
    const [selectionModalActive, setSelectionModalActive] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(null);
    const [cancelModalActive, setCancelModalActive] = useState(false);
    const [pendingCancellation, setPendingCancellation] = useState(null);
    const [transferModalActive, setTransferModalActive] = useState(false);
    const [pendingTransfer, setPendingTransfer] = useState(null);
    const [editModalActive, setEditModalActive] = useState(false);
    const [pendingEdit, setPendingEdit] = useState(null);
    const [refreshingSlotState, setRefreshingSlotState] = useState(null);
    const [isDesktopView, setIsDesktopView] = useState(() => {
        if (typeof window === 'undefined') {
            return true;
        }
        return window.matchMedia('(min-width:700px)').matches;
    });
    const [roomPageState, setRoomPageState] = useState(1);
    const [tableWrapWidth, setTableWrapWidth] = useState(0);

    const selectedDateText = useMemo(() => formatDateForApi(selectedDate), [selectedDate]);
    const bookingsPerms = Number(userAppData?.bookingsData?.perms ?? 0);
    const canApplyDate = pendingDateText !== selectedDateText;

    useEffect(() => {
        if (bookingsPerms !== 1) {
            navigate('/app');
        }
    }, [bookingsPerms, navigate]);

    useEffect(() => {
        setPendingDateText(selectedDateText);
    }, [selectedDateText]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(min-width:700px)');
        const updateDesktopState = (event) => {
            setIsDesktopView(Boolean(event.matches));
        };

        setIsDesktopView(Boolean(mediaQuery.matches));
        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', updateDesktopState);
            return () => mediaQuery.removeEventListener('change', updateDesktopState);
        }

        mediaQuery.addListener(updateDesktopState);
        return () => mediaQuery.removeListener(updateDesktopState);
    }, []);

    const loadDayData = useCallback(async (showGlobalLoading = true) => {
        if (showGlobalLoading) {
            setIsLoaded(false);
        }
        try {
            const response = await getBookingTsDay(selectedDateText);
            setRoomsMetadata(response?.roomsMetadata || { rooms: [], groups: [], subGroups: [] });
            setBookingsByRoom(response?.bookingsByRoom || {});
            setDisplaySettings(response?.displaySettings || {
                dayStartTime: '08:00',
                dayEndTime: '18:00',
                displaySlotMins: 15,
            });
            setApiError(false);
            setIsLoaded(true);
        } catch (error) {
            console.error('Failed to load BOOKINGSTS day data', error);
            setApiError(true);
            setIsLoaded(true);
        }
    }, [selectedDateText]);

    useEffect(() => {
        loadDayData();
    }, [loadDayData]);

    const metadataDisplaySlot = useMemo(() => {
        const groups = Array.isArray(roomsMetadata?.groups) ? roomsMetadata.groups : [];
        const firstWithSlot = groups.find((group) => Object.prototype.hasOwnProperty.call(group || {}, 'displaySlotMins'));
        const parsed = Number(firstWithSlot?.displaySlotMins);
        return [5, 10, 15].includes(parsed) ? parsed : null;
    }, [roomsMetadata?.groups]);
    const pageHeading = useMemo(() => {
        const groups = Array.isArray(roomsMetadata?.groups) ? roomsMetadata.groups : [];
        const firstGroupName = String(groups?.[0]?.name || '').trim();
        return firstGroupName || 'Rooms';
    }, [roomsMetadata?.groups]);

    const daySlotSize = Number(displaySettings?.displaySlotMins ?? metadataDisplaySlot ?? 15);
    const daySlots = useMemo(
        () => buildDisplaySlots(displaySettings?.dayStartTime, displaySettings?.dayEndTime, daySlotSize),
        [displaySettings?.dayStartTime, displaySettings?.dayEndTime, daySlotSize],
    );

    const roomLayouts = useMemo(
        () => buildRoomLayouts(roomsMetadata?.rooms || [], bookingsByRoom, daySlots, daySlotSize),
        [roomsMetadata, bookingsByRoom, daySlots, daySlotSize],
    );

    const dragTooltip = useMemo(() => {
        if (!dragSelection || daySlots.length === 0) {
            return null;
        }

        const lowerIndex = Math.min(dragSelection.startIndex, dragSelection.currentIndex);
        const upperIndex = Math.max(dragSelection.startIndex, dragSelection.currentIndex);
        const startMinutesRaw = daySlots[lowerIndex];
        const endMinutesRaw = daySlots[upperIndex] + daySlotSize;

        const startMinutes = snapDownToFive(startMinutesRaw);
        const endMinutes = Math.max(startMinutes + 5, snapUpToFive(endMinutesRaw));
        const totalMinutes = Math.max(5, endMinutes - startMinutes);

        return {
            startTime: minutesToTime(startMinutes),
            endTime: minutesToTime(endMinutes),
            totalTime: formatDuration(totalMinutes),
        };
    }, [dragSelection, daySlots, daySlotSize]);

    function canCreateInRoom(roomPerm) {
        return roomPerm === 'edit' || roomPerm === 'manage';
    }

    function canCancelBooking(booking, room) {
        const ownBooking = Boolean(booking?.ownBooking);
        const roomPermission = String(booking?.roomPerm || room?.roomPerm || '').trim().toLowerCase();
        return ownBooking || roomPermission === 'manage';
    }

    function canTransferBooking(booking, room) {
        if (String(booking?.origin || '').toUpperCase() === 'TIMETABLED') {
            return false;
        }
        return canCancelBooking(booking, room);
    }

    function canEditBooking(booking, room) {
        if (String(booking?.origin || '').toUpperCase() === 'TIMETABLED') {
            return false;
        }
        return canCancelBooking(booking, room);
    }

    /**
     * Apply the typed date only after the user explicitly confirms the change.
     */
    function applyPendingDate() {
        const parsedDate = parseDateInputValue(pendingDateText);

        if (!parsedDate) {
            toast.error('❌ Please enter a valid date before loading bookings.', toastConfig);
            return;
        }

        setSelectedDate(parsedDate);
    }

    function isSelectedCell(roomGuid, rowIndex) {
        if (!dragSelection || dragSelection.roomGuid !== roomGuid) {
            return false;
        }

        const lower = Math.min(dragSelection.startIndex, dragSelection.currentIndex);
        const upper = Math.max(dragSelection.startIndex, dragSelection.currentIndex);
        return rowIndex >= lower && rowIndex <= upper;
    }

    function formatDuration(totalMinutes) {
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;

        if (hours > 0 && mins > 0) {
            return `${hours}h ${mins}m`;
        }
        if (hours > 0) {
            return `${hours}h`;
        }
        return `${mins}m`;
    }

    function startDrag(room, rowIndex, mouseEvent) {
        if (!canCreateInRoom(room.roomPerm)) {
            return;
        }

        if (mouseEvent?.clientX && mouseEvent?.clientY) {
            setDragCursor({ x: mouseEvent.clientX, y: mouseEvent.clientY });
            dragCursorRef.current = { x: mouseEvent.clientX, y: mouseEvent.clientY };
        }

        setDragSelection({
            roomGuid: room.roomId,
            roomName: room.name,
            roomAllowAttendees: canAddAttendeesForRoom(room),
            startIndex: rowIndex,
            currentIndex: rowIndex,
        });
    }

    function updateDrag(roomGuid, rowIndex) {
        if (!dragSelection || dragSelection.roomGuid !== roomGuid) {
            return;
        }

        setDragSelection((prevState) => {
            if (!prevState) {
                return prevState;
            }
            return {
                ...prevState,
                currentIndex: rowIndex,
            };
        });
    }

    function finalizeSelection() {
        const selection = dragSelection;
        setDragSelection(null);

        if (!selection) {
            return;
        }

        const lowerIndex = Math.min(selection.startIndex, selection.currentIndex);
        const upperIndex = Math.max(selection.startIndex, selection.currentIndex);

        const startMinutesRaw = daySlots[lowerIndex];
        const endMinutesRaw = daySlots[upperIndex] + daySlotSize;

        const startMinutes = snapDownToFive(startMinutesRaw);
        const endMinutes = Math.max(startMinutes + 5, snapUpToFive(endMinutesRaw));

        const selectionPayload = {
            date: selectedDateText,
            roomGuid: selection.roomGuid,
            roomName: selection.roomName,
            roomAllowAttendees: Boolean(selection.roomAllowAttendees),
            startTime: minutesToTime(startMinutes),
            endTime: minutesToTime(endMinutes),
        };
        setPendingSelection(selectionPayload);
        setSelectionModalActive(true);
    }

    /**
     * Open booking modal for a single tapped slot (mobile interaction path).
     * @param {object} room - Room metadata for the selected cell.
     * @param {number} rowIndex - Slot row index that was tapped.
     * @returns {void}
     */
    function openSingleSlotSelection(room, rowIndex) {
        if (!canCreateInRoom(room?.roomPerm)) {
            return;
        }

        const startMinutes = Number(daySlots?.[rowIndex]);
        if (!Number.isFinite(startMinutes)) {
            return;
        }

        const endMinutes = startMinutes + daySlotSize;
        setPendingSelection({
            date: selectedDateText,
            roomGuid: room.roomId,
            roomName: room.name,
            roomAllowAttendees: canAddAttendeesForRoom(room),
            startTime: minutesToTime(startMinutes),
            endTime: minutesToTime(endMinutes),
        });
        setSelectionModalActive(true);
    }

    async function submitBookingSelection({
        title,
        notes,
        proxyFor,
        attendees,
        omitNotesInInvite,
        omitOtherAttendeesInInvite,
        startTime,
        endTime,
    }) {
        if (!pendingSelection) {
            return;
        }

        const attendeesPayload = {
            internal: Array.isArray(attendees?.internal) ? attendees.internal : [],
            external: Array.isArray(attendees?.external) ? attendees.external : [],
        };

        const payload = {
            date: pendingSelection.date,
            roomGuid: pendingSelection.roomGuid,
            startTime: startTime || pendingSelection.startTime,
            endTime: endTime || pendingSelection.endTime,
            title: title || '',
            notes: notes || '',
            proxyFor: proxyFor || null,
            attendees: attendeesPayload,
            omitNotesInInvite: Boolean(omitNotesInInvite),
            omitOtherAttendeesInInvite: Boolean(omitOtherAttendeesInInvite),
        };

        try {
            const response = await createBookingTs(payload);

            if (response?.data === 'conflict') {
                toast.error('\u274c This time range is already booked', toastConfig);
                await loadDayData(false);
                throw new Error('Timeslot conflict');
            }

            if (!response?.bookingGuid) {
                toast.error('\u274c Booking request failed. Please try again.', toastConfig);
                throw new Error('Missing bookingGuid');
            }

            toast.success('\u2705 Booking created', toastConfig);
            setRefreshingSlotState({
                roomGuid: pendingSelection.roomGuid,
                startMins: timeToMinutes(payload.startTime),
                endMins: timeToMinutes(payload.endTime),
                visual: 'free',
            });
            setPendingSelection(null);
            await loadDayData(false);
        } catch (error) {
            console.error('Failed to create booking', error);
            toast.error('\u274c Booking request failed. Please try again.', toastConfig);
            throw error;
        } finally {
            setRefreshingSlotState(null);
        }
    }

    useEffect(() => {
        function onMouseUp() {
            if (!dragSelection) {
                return;
            }
            finalizeSelection();
        }
        function onMouseMove(event) {
            if (!dragSelection) {
                return;
            }
            dragCursorRef.current = { x: event.clientX, y: event.clientY };
            setDragCursor({ x: event.clientX, y: event.clientY });
        }

        window.addEventListener('mouseup', onMouseUp);
        window.addEventListener('mousemove', onMouseMove);
        return () => {
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('mousemove', onMouseMove);
        };
    // finalizeSelection intentionally reads current state and does not need to be a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dragSelection]);

    useEffect(() => {
        if (!dragSelection) {
            if (autoScrollFrameRef.current) {
                cancelAnimationFrame(autoScrollFrameRef.current);
                autoScrollFrameRef.current = null;
            }
            return undefined;
        }

        const container = tableWrapRef.current;
        if (!container) {
            return undefined;
        }

        const edgeThresholdPx = 48;
        const minStepPx = 1.5;
        const maxStepPx = 4.5;

        const tick = () => {
            const rect = container.getBoundingClientRect();
            const { x, y } = dragCursorRef.current;
            let scrollDelta = 0;

            if (y > rect.bottom - edgeThresholdPx) {
                const intensity = Math.min(1, (y - (rect.bottom - edgeThresholdPx)) / edgeThresholdPx);
                scrollDelta = minStepPx + (intensity * (maxStepPx - minStepPx));
            } else if (y < rect.top + edgeThresholdPx) {
                const intensity = Math.min(1, ((rect.top + edgeThresholdPx) - y) / edgeThresholdPx);
                scrollDelta = -(minStepPx + (intensity * (maxStepPx - minStepPx)));
            }

            if (scrollDelta !== 0) {
                const previousScrollTop = container.scrollTop;
                container.scrollTop = previousScrollTop + scrollDelta;

                if (container.scrollTop !== previousScrollTop) {
                    const hoveredElement = document.elementFromPoint(x, y);
                    const selectableCell = hoveredElement?.closest?.('td[data-room-guid][data-row-index]');
                    if (selectableCell) {
                        const hoveredRoomGuid = selectableCell.getAttribute('data-room-guid');
                        const hoveredRowIndex = Number(selectableCell.getAttribute('data-row-index'));
                        if (hoveredRoomGuid && Number.isInteger(hoveredRowIndex)) {
                            setDragSelection((previousSelection) => {
                                if (!previousSelection || previousSelection.roomGuid !== hoveredRoomGuid) {
                                    return previousSelection;
                                }
                                if (previousSelection.currentIndex === hoveredRowIndex) {
                                    return previousSelection;
                                }
                                return {
                                    ...previousSelection,
                                    currentIndex: hoveredRowIndex,
                                };
                            });
                        }
                    }
                }
            }

            autoScrollFrameRef.current = requestAnimationFrame(tick);
        };

        autoScrollFrameRef.current = requestAnimationFrame(tick);
        return () => {
            if (autoScrollFrameRef.current) {
                cancelAnimationFrame(autoScrollFrameRef.current);
                autoScrollFrameRef.current = null;
            }
        };
    }, [dragSelection]);

    async function handleCancelBooking(booking, room) {
        if (!canCancelBooking(booking, room)) {
            return;
        }

        setPendingCancellation({
            bookingGuid: booking.bookingGuid,
            roomGuid: room.roomId,
            roomName: room.name,
            date: selectedDateText,
            startTime: booking.startTime,
            endTime: booking.endTime,
            bookedByDisplayName: booking.bookedByDisplayName || 'Unknown user',
        });
        setCancelModalActive(true);
    }

    async function handleOpenTransferBooking(booking, room) {
        if (!canTransferBooking(booking, room)) {
            return;
        }

        setPendingTransfer({
            bookingGuid: booking.bookingGuid,
            roomGuid: room.roomId,
            roomName: room.name,
            date: selectedDateText,
            startTime: booking.startTime,
            endTime: booking.endTime,
        });
        setTransferModalActive(true);
    }

    async function handleOpenEditBooking(booking, room) {
        if (!canEditBooking(booking, room)) {
            return;
        }

        setPendingEdit({
            bookingGuid: booking.bookingGuid,
            roomGuid: room.roomId,
            roomName: room.name,
            date: booking.date || selectedDateText,
            startTime: booking.startTime,
            endTime: booking.endTime,
            title: booking.title || '',
            notes: booking.notes || '',
            attendees: booking.attendees || { internal: [], external: [] },
            attendeeInternalPeople: Array.isArray(booking.attendeeInternalPeople) ? booking.attendeeInternalPeople : [],
            attendeeDisplayNames: Array.isArray(booking.attendeeDisplayNames) ? booking.attendeeDisplayNames : [],
            attendeeInviteOptions: booking.attendeeInviteOptions || {
                omitNotesInInvite: false,
                omitOtherAttendeesInInvite: false,
            },
            roomAllowAttendees: canAddAttendeesForRoom(room),
            bookedByCogId: booking.bookedByCogId || '',
            bookedForCogId: booking.bookedForCogId || '',
            bookedByDisplayName: booking.bookedByDisplayName || '',
            bookedForDisplayName: booking.bookedForDisplayName || '',
            origin: booking.origin || 'MANUAL',
        });
        setEditModalActive(true);
    }

    async function checkEditAvailability({ date, startTime, endTime }) {
        if (!pendingEdit) {
            return { available: false, message: 'No booking selected.' };
        }

        const response = await checkBookingTsAvailability({
            date,
            sourceDate: pendingEdit.date,
            roomGuid: pendingEdit.roomGuid,
            bookingGuid: pendingEdit.bookingGuid,
            targetRoomGuid: pendingEdit.roomGuid,
            startTime,
            endTime,
        });

        return {
            available: Boolean(response?.available),
            message: String(response?.message || ''),
        };
    }

    async function submitBookingEdit(editOptions = {}) {
        if (!pendingEdit) {
            return;
        }

        try {
            const response = await editBookingTs({
                date: pendingEdit.date,
                roomGuid: pendingEdit.roomGuid,
                bookingGuid: pendingEdit.bookingGuid,
                title: String(editOptions?.title || ''),
                notes: String(editOptions?.notes || ''),
                attendees: {
                    internal: Array.isArray(editOptions?.attendees?.internal) ? editOptions.attendees.internal : [],
                    external: Array.isArray(editOptions?.attendees?.external) ? editOptions.attendees.external : [],
                },
                omitNotesInInvite: Boolean(editOptions?.omitNotesInInvite),
                omitOtherAttendeesInInvite: Boolean(editOptions?.omitOtherAttendeesInInvite),
                changeDateTime: Boolean(editOptions?.changeDateTime),
                newDate: String(editOptions?.newDate || ''),
                newStartTime: String(editOptions?.newStartTime || ''),
                newEndTime: String(editOptions?.newEndTime || ''),
                removedAttendeeReason: String(editOptions?.removedAttendeeReason || ''),
            });

            if (!response?.success) {
                toast.error('\u274c Edit request failed. Please try again.', toastConfig);
                throw new Error('Edit failed');
            }

            toast.success('\u2705 Booking updated', toastConfig);
            setRefreshingSlotState({
                roomGuid: pendingEdit.roomGuid,
                startMins: timeToMinutes(pendingEdit.startTime),
                endMins: timeToMinutes(pendingEdit.endTime),
                visual: 'booked',
            });
            setEditModalActive(false);
            setPendingEdit(null);
            await loadDayData(false);
        } catch (error) {
            console.error('Failed to edit booking', error);
            toast.error('\u274c Edit request failed. Please try again.', toastConfig);
            throw error;
        } finally {
            setRefreshingSlotState(null);
        }
    }

    async function submitBookingCancellation(cancelOptions = {}) {
        if (!pendingCancellation) {
            return;
        }

        try {
            const response = await cancelBookingTs({
                date: pendingCancellation.date,
                roomGuid: pendingCancellation.roomGuid,
                bookingGuid: pendingCancellation.bookingGuid,
                notifyAttendees: Boolean(cancelOptions?.notifyAttendees),
                notifyReason: String(cancelOptions?.notifyReason || '').trim(),
            });

            if (!response?.success) {
                toast.error('\u274c Cancel request failed. Please try again.', toastConfig);
                throw new Error('Cancel failed');
            }

            toast.success('\u2705 Booking cancelled', toastConfig);
            setRefreshingSlotState({
                roomGuid: pendingCancellation.roomGuid,
                startMins: timeToMinutes(pendingCancellation.startTime),
                endMins: timeToMinutes(pendingCancellation.endTime),
                visual: 'booked',
            });
            setPendingCancellation(null);
            await loadDayData(false);
        } catch (error) {
            console.error('Failed to cancel booking', error);
            toast.error('\u274c Cancel request failed. Please try again.', toastConfig);
            throw error;
        } finally {
            setRefreshingSlotState(null);
        }
    }

    async function submitBookingTransfer(transferOptions = {}) {
        if (!pendingTransfer) {
            return;
        }

        try {
            const response = await transferBookingTs({
                date: pendingTransfer.date,
                roomGuid: pendingTransfer.roomGuid,
                bookingGuid: pendingTransfer.bookingGuid,
                targetRoomGuid: transferOptions?.targetRoomGuid,
            });

            if (!response?.success) {
                toast.error('\u274c Transfer request failed. Please try again.', toastConfig);
                throw new Error('Transfer failed');
            }

            toast.success('\u2705 Booking transferred to ' + (transferOptions?.targetRoomName || 'selected room'), toastConfig);
            setPendingTransfer(null);
            await loadDayData(false);
        } catch (error) {
            console.error('Failed to transfer booking', error);
            toast.error('\u274c Transfer request failed. Please try again.', toastConfig);
            throw error;
        }
    }

    const contentLoaded = isLoaded && !apiError;
    const rooms = roomsMetadata?.rooms || [];
    const roomPageSize = isDesktopView ? DESKTOP_ROOM_PAGE_SIZE : MOBILE_ROOM_PAGE_SIZE;
    const roomPages = useMemo(
        () => Math.max(1, Math.ceil(rooms.length / roomPageSize)),
        [rooms.length, roomPageSize],
    );
    const showRoomPagination = roomPages > 1;
    const visibleRooms = useMemo(() => {
        const startIndex = (roomPageState - 1) * roomPageSize;
        return rooms.slice(startIndex, startIndex + roomPageSize);
    }, [rooms, roomPageState, roomPageSize]);
    const desktopRoomColumnWidth = useMemo(() => {
        if (!isDesktopView) {
            return 120;
        }

        const roomCount = visibleRooms.length;
        if (roomCount <= 0) {
            return 96;
        }

        const minWidthPx = 120;
        const maxWidthPx = 320;
        const widthByCount = minWidthPx + ((DESKTOP_ROOM_PAGE_SIZE - roomCount) / (DESKTOP_ROOM_PAGE_SIZE - 1)) * (maxWidthPx - minWidthPx);
        const timeColumnWidthPx = 90;
        const tableGutterPx = 18;
        const availableWidth = Math.max(0, tableWrapWidth - timeColumnWidthPx - tableGutterPx);
        const widthByContainer = roomCount > 0 ? Math.floor(availableWidth / roomCount) : minWidthPx;
        const preferredWidth = Math.max(widthByCount, widthByContainer);

        return Math.round(Math.max(minWidthPx, Math.min(maxWidthPx, preferredWidth)));
    }, [isDesktopView, visibleRooms.length, tableWrapWidth]);

    useEffect(() => {
        const safePage = Math.min(roomPageState, roomPages);
        if (safePage !== roomPageState) {
            setRoomPageState(safePage);
        }
    }, [roomPageState, roomPages]);

    useEffect(() => {
        const tableWrap = tableWrapRef.current;
        if (!tableWrap) {
            return undefined;
        }

        const updateWidth = () => {
            setTableWrapWidth(Math.floor(tableWrap.clientWidth || 0));
        };

        updateWidth();
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(() => updateWidth());
            observer.observe(tableWrap);
            return () => observer.disconnect();
        }

        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [rooms.length, isDesktopView]);

    useEffect(() => {
        const tableWrap = tableWrapRef.current;
        if (tableWrap) {
            tableWrap.scrollLeft = 0;
        }
    }, [roomPageState, selectedDateText]);
    const bookingTimeOptions = useMemo(() => {
        if (!Array.isArray(daySlots) || daySlots.length === 0) {
            return [];
        }

        const optionMinutes = [...daySlots, daySlots[daySlots.length - 1] + daySlotSize];
        return optionMinutes.map((minutesValue) => minutesToTime(minutesValue));
    }, [daySlots, daySlotSize]);

    if (!isLoaded) {
        return <LoadingComp />;
    }

    if (apiError) {
        return <ApiError />;
    }

    return (
        <div className='internal_main_div bookingstsMain'>
            <BookingstsBookingModal
                modalActive={selectionModalActive}
                setModalActive={(activeState) => {
                    setSelectionModalActive(activeState);
                    if (!activeState) {
                        setPendingSelection(null);
                    }
                }}
                roomName={pendingSelection?.roomName || ''}
                date={pendingSelection?.date || selectedDateText}
                startTime={pendingSelection?.startTime || ''}
                endTime={pendingSelection?.endTime || ''}
                allowTimeEdit={!isDesktopView}
                timeOptions={bookingTimeOptions}
                proxyAllowed={Boolean(userAppData?.bookingsData?.proxyBookAllowed)}
                attendeesAllowed={Boolean(pendingSelection?.roomAllowAttendees)}
                currentUserCogId={String(userAppData?.cogId || '')}
                onSubmit={submitBookingSelection}
            />
            <BookingstsCancelModal
                modalActive={cancelModalActive}
                setModalActive={(activeState) => {
                    setCancelModalActive(activeState);
                    if (!activeState) {
                        setPendingCancellation(null);
                    }
                }}
                roomName={pendingCancellation?.roomName || ''}
                date={pendingCancellation?.date || selectedDateText}
                startTime={pendingCancellation?.startTime || ''}
                endTime={pendingCancellation?.endTime || ''}
                bookedByDisplayName={pendingCancellation?.bookedByDisplayName || ''}
                onSubmit={submitBookingCancellation}
            />
            <BookingstsTransferModal
                modalActive={transferModalActive}
                setModalActive={(activeState) => {
                    setTransferModalActive(activeState);
                    if (!activeState) {
                        setPendingTransfer(null);
                    }
                }}
                roomGuid={pendingTransfer?.roomGuid || ''}
                bookingGuid={pendingTransfer?.bookingGuid || ''}
                currentRoomName={pendingTransfer?.roomName || ''}
                date={pendingTransfer?.date || selectedDateText}
                startTime={pendingTransfer?.startTime || ''}
                endTime={pendingTransfer?.endTime || ''}
                onSubmit={submitBookingTransfer}
            />
            <BookingstsEditModal
                modalActive={editModalActive}
                setModalActive={(activeState) => {
                    setEditModalActive(activeState);
                    if (!activeState) {
                        setEditModalActive(false);
            setPendingEdit(null);
                    }
                }}
                bookingData={pendingEdit}
                attendeesAllowed={Boolean(pendingEdit?.roomAllowAttendees)}
                onCheckAvailability={checkEditAvailability}
                onSubmit={submitBookingEdit}
            />

            <div className='bookingstsHeaderWrap'>
                <Header text={pageHeading} />
            </div>

            <div className='bookingstsPageContent'>
                <div className='bookingstsControlRow'>
                    <div className='bookingstsControlLeft'>
                        <label className='bookingstsControlLabel' htmlFor='bookingstsDate'>Date:</label>
                        <input
                            id='bookingstsDate'
                            className='bookingstsDateInput'
                            type='date'
                            value={pendingDateText}
                            onChange={(event) => {
                                setPendingDateText(event.target.value);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    applyPendingDate();
                                }
                            }}
                        />
                        <button
                            type='button'
                            className='bookingstsApplyDateButton'
                            onClick={applyPendingDate}
                            disabled={!canApplyDate}
                        >
                            Load Day
                        </button>
                    </div>
                    {showRoomPagination && (
                        <div className='bookingstsRoomsPagination' aria-label='Rooms pagination'>
                            <button
                                type='button'
                                className='bookingstsRoomsPaginationButton'
                                onClick={() => {
                                    setRoomPageState((previousPage) => (
                                        previousPage > 1 ? previousPage - 1 : roomPages
                                    ));
                                }}
                                title='Previous room page'
                            >
                                <FaArrowLeft />
                            </button>
                            <div className='bookingstsRoomsPaginationText'>
                                {roomPageState} / {roomPages}
                            </div>
                            <button
                                type='button'
                                className='bookingstsRoomsPaginationButton'
                                onClick={() => {
                                    setRoomPageState((previousPage) => (
                                        previousPage < roomPages ? previousPage + 1 : 1
                                    ));
                                }}
                                title='Next room page'
                            >
                                <FaArrowRight />
                            </button>
                        </div>
                    )}
                    <div className='bookingstsControlRight'>
                        <span className='bookingstsSettingText'>Display slot: {daySlotSize} mins</span>
                    </div>
                </div>

                {contentLoaded && rooms.length === 0 && (
                    <div className='bookingstsMessage'>No permitted rooms for this module.</div>
                )}

                {contentLoaded && rooms.length > 0 && daySlots.length > 0 && (
                <div className='bookingstsTableWrap' ref={tableWrapRef}>
                        <table className='bookingstsTable' style={{ '--bookingsts-room-col-width': `${desktopRoomColumnWidth}px` }}>
                            <colgroup>
                                <col className='bookingstsTimeColumn' />
                                {visibleRooms.map((room) => (
                                    <col key={`room-col-${room.roomId}`} className='bookingstsRoomColumn' />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className='bookingstsHeaderTime'>Time</th>
                                    {visibleRooms.map((room) => (
                                        <th key={room.roomId} className='bookingstsHeaderRoom'>
                                            {room.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {daySlots.map((slotStart, rowIndex) => (
                                    <tr key={`slot-${slotStart}`}>
                                        <td className='bookingstsTimeCell'>{minutesToTime(slotStart)}</td>
                                        {visibleRooms.map((room) => {
                                            const layout = roomLayouts?.[room.roomId] || { startCells: {}, covered: new Set() };
                                            const activeRefreshForRoom = refreshingSlotState && refreshingSlotState.roomGuid === room.roomId;
                                            if (activeRefreshForRoom) {
                                                const dayStartMinutes = daySlots[0] ?? 0;
                                                const refreshStartIndex = Math.max(
                                                    0,
                                                    Math.floor((Number(refreshingSlotState.startMins) - dayStartMinutes) / daySlotSize),
                                                );
                                                const refreshEndIndex = Math.min(
                                                    daySlots.length,
                                                    Math.max(
                                                        refreshStartIndex + 1,
                                                        Math.ceil((Number(refreshingSlotState.endMins) - dayStartMinutes) / daySlotSize),
                                                    ),
                                                );

                                                if (rowIndex >= refreshStartIndex && rowIndex < refreshEndIndex) {
                                                    if (rowIndex !== refreshStartIndex) {
                                                        return null;
                                                    }

                                                    const refreshClass = refreshingSlotState.visual === 'booked'
                                                        ? 'bookingstsBookedCell bookingstsRefreshingCell'
                                                        : 'bookingstsFreeCell bookingstsRefreshingCell';

                                                    return (
                                                        <td
                                                            key={`${room.roomId}-${slotStart}-refresh`}
                                                            rowSpan={Math.max(1, refreshEndIndex - refreshStartIndex)}
                                                            className={refreshClass}
                                                            title='Refreshing booking'
                                                        >
                                                            <div className='bookingstsRefreshingInner'>
                                                                <CgSpinner className='bookingstsCellRefreshSpinner' />
                                                            </div>
                                                        </td>
                                                    );
                                                }
                                            }

                                            if (layout.covered.has(rowIndex)) {
                                                return null;
                                            }

                                                const bookingCell = layout.startCells[rowIndex];
                                                if (bookingCell) {
                                                    const booking = bookingCell.booking;
                                                    const canCancel = canCancelBooking(booking, room);
                                                    const canTransfer = canTransferBooking(booking, room);
                                                    const bookingDensity = bookingCell.rowSpan <= 2
                                                        ? 'compact'
                                                        : bookingCell.rowSpan <= 4
                                                            ? 'standard'
                                                            : 'expanded';

                                                return (
                                                    <td
                                                        key={`${room.roomId}-${slotStart}`}
                                                        rowSpan={bookingCell.rowSpan}
                                                        className='bookingstsBookedCell'
                                                        title='Booking'
                                                    >
                                                        <div className={`bookingstsBookedInner bookingstsBookedInner${bookingDensity.charAt(0).toUpperCase()}${bookingDensity.slice(1)}`}>
                                                            <div className={booking.origin === 'PROXY' ? 'bookingstsBookedName bookingstsBookedNameProxy' : 'bookingstsBookedName'}>
                                                                {booking.bookedForDisplayName || booking.bookedByDisplayName || 'Unknown user'}
                                                            </div>
                                                            {booking.origin === 'PROXY' && bookingDensity !== 'compact' && (
                                                                <div className='bookingstsBookedProxyBy'>(Booked by {booking.bookedByDisplayName || 'Unknown user'})</div>
                                                            )}
                                                            {bookingDensity !== 'compact' && (
                                                                <>
                                                                    <div className='bookingstsBookedFieldLabel'>Meeting Title:</div>
                                                                    <div className='bookingstsBookedTitle'>{booking.title || 'Booked'}</div>
                                                                </>
                                                            )}
                                                            <div className='bookingstsBookedMeta'>{booking.startTime} - {booking.endTime}</div>
                                                            {bookingDensity === 'expanded' && Array.isArray(booking.attendeeDisplayNames) && booking.attendeeDisplayNames.length > 0 && (
                                                                <div className='bookingstsBookedMeta'>
                                                                    Attendees: {booking.attendeeDisplayNames.join(', ')}
                                                                </div>
                                                            )}
                                                            {bookingDensity === 'expanded' && Boolean(booking.notes) && (
                                                                <>
                                                                    <div className='bookingstsBookedFieldLabel'>Notes:</div>
                                                                    <div className='bookingstsBookedMeta'>{booking.notes}</div>
                                                                </>
                                                            )}
                                                            {(canEditBooking(booking, room) || canTransfer || canCancel) && (
                                                                <div className='bookingstsBookedActions'>
                                                                    {canEditBooking(booking, room) && (
                                                                        <button
                                                                            type='button'
                                                                            className='bookingstsEditIconButton'
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleOpenEditBooking(booking, room);
                                                                            }}
                                                                            title='Edit booking'
                                                                        >
                                                                            <FaPencil />
                                                                        </button>
                                                                    )}
                                                                    {canTransfer && (
                                                                        <button
                                                                            type='button'
                                                                            className='bookingstsTransferIconButton'
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleOpenTransferBooking(booking, room);
                                                                            }}
                                                                            title='Transfer booking'
                                                                        >
                                                                            <FaExchangeAlt />
                                                                        </button>
                                                                    )}
                                                                    {canCancel && (
                                                                        <button
                                                                            type='button'
                                                                            className='bookingstsCancelIconButton'
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleCancelBooking(booking, room);
                                                                            }}
                                                                            title='Cancel booking'
                                                                        >
                                                                            <FiTrash2 />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            }

                                            return (
                                                <td
                                                    key={`${room.roomId}-${slotStart}`}
                                                    className={isSelectedCell(room.roomId, rowIndex) ? 'bookingstsFreeCellSelected' : 'bookingstsFreeCell'}
                                                    data-room-guid={room.roomId}
                                                    data-row-index={rowIndex}
                                                    onMouseDown={isDesktopView ? (event) => startDrag(room, rowIndex, event) : undefined}
                                                    onMouseEnter={isDesktopView ? () => updateDrag(room.roomId, rowIndex) : undefined}
                                                    onClick={!isDesktopView ? () => openSingleSlotSelection(room, rowIndex) : undefined}
                                                />
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {dragTooltip && (
                <div
                    className='bookingstsDragTooltip'
                    style={{ left: `${dragCursor.x + 14}px`, top: `${dragCursor.y + 14}px` }}
                >
                    <div><strong>Start:</strong> {dragTooltip.startTime}</div>
                    <div><strong>Finish:</strong> {dragTooltip.endTime}</div>
                    <div><strong>Total:</strong> {dragTooltip.totalTime}</div>
                </div>
            )}
        </div>
    );
}





